/**
 * Investment Item Types
 *
 * Type definitions for the investment items module.
 * Investment items represent CAPEX ideas/investment lines that can be converted to renovation projects.
 */

import { BuildingDTO } from "shared/types/building";
import { UnitDTO } from "shared/types/unit";

/**
 * Priority levels for investment items
 */
export enum InvestmentItemPriority {
  LOW = "LOW",
  MEDIUM = "MEDIUM",
  HIGH = "HIGH",
  CRITICAL = "CRITICAL",
}

/**
 * Status values for investment items workflow
 */
export enum InvestmentItemStatus {
  ONTWERP = "ONTWERP",
  IN_BEOORDELING = "IN_BEOORDELING",
  GOEDGEKEURD = "GOEDGEKEURD",
  AFGEWEZEN = "AFGEWEZEN",
  IN_PROJECT = "IN_PROJECT",
  /**
   * Terminal state for a finished line. Triggers a RELEASE on the bucket
   * for any unspent remainder (estimatedCostExclVat − allocatedBudget).
   */
  CLOSED = "CLOSED",
}

/**
 * Budget labels for categorizing the budget source/type of an investment item
 */
export enum InvestmentItemBudgetLabel {
  STRATEGISCH = "STRATEGISCH",
  ONVOORZIEN = "ONVOORZIEN",
}

/**
 * Audit log change types
 */
export enum AuditLogChangeType {
  STATUS_CHANGE = "STATUS_CHANGE",
  BUDGET_CHANGE = "BUDGET_CHANGE",
  ALLOCATION_CHANGE = "ALLOCATION_CHANGE",
  CREATED = "CREATED",
  BUDGET_TRANSFER = "BUDGET_TRANSFER",
}

/**
 * Audit log entry for tracking investment item changes
 */
export interface InvestmentItemAuditLog {
  id: number;
  investmentItemId: number;
  changeType: AuditLogChangeType;
  oldStatus: InvestmentItemStatus | null;
  newStatus: InvestmentItemStatus | null;
  oldValue: number | null;
  newValue: number | null;
  reason: string;
  changedByUserId: string;
  sourceItemId: number | null; // For BUDGET_TRANSFER: reference to source item
  createdAt: string;
}

/**
 * Renovation label for categorizing investment items
 */
export interface RenovationLabel {
  id: number;
  name: string;
  type: string;
}

/**
 * Project part reference (minimal info for display)
 *
 * The FE row uses these to render per-assignment chips on the
 * "Gebruikt in projectonderdelen" cell:
 *   RENO-{projectId}-O{partId} · {label} · €{amount}
 */
export interface ProjectPartRef {
  id: number;
  title: string;
  label?: { id: number; name: string; type?: string };
}

/**
 * Renovation project reference (minimal info for display)
 */
export interface RenovationProjectRef {
  id: number;
  title: string;
}

/**
 * Assignment linking an investment item to a project part
 * This is the new system replacing the old single-project relation
 */
export interface InvestmentItemPartAssignment {
  id: number;
  project: RenovationProjectRef;
  part?: ProjectPartRef;
  assignedAmount: number;
  /**
   * Portion of `assignedAmount` that is covered by the strategic budget.
   * Persisted by the BE. Defaults to 0 on legacy rows (before migration).
   */
  strategicAmount: number;
  /**
   * Portion of `assignedAmount` that overflows the strategic budget.
   * Persisted by the BE. Defaults to 0 on legacy rows (before migration).
   */
  overflowAmount: number;
  notes?: string;
  createdAt: string;
}

/**
 * Main investment item entity
 */
export interface InvestmentItem {
  id: number;
  title: string;
  description: string;
  estimatedCostExclVat: number;
  allocatedBudget: number;
  status: InvestmentItemStatus;
  budgetLabel?: InvestmentItemBudgetLabel | null;
  buildingId: string;
  building?: BuildingDTO;
  units?: UnitDTO[];
  label?: RenovationLabel;
  createdByUserId: string;
  responsibleUserId: string;
  /** @deprecated Use partAssignments instead */
  renovationProject?: { id: number; title?: string };
  /** New system: assignments to project parts */
  partAssignments?: InvestmentItemPartAssignment[];
  /**
   * Strategic budget bucket this line draws from. `null` / undefined means
   * the line is in "overflow" — not yet attached to an approved pot.
   *
   * Legacy single-pot pointer; `bucketAllocations` is the canonical multi-
   * pot model. The BE keeps `bucket` synced with the primary (first non-
   * null) allocation for backward compatibility.
   */
  bucket?: {
    id: number;
    name: string;
    isApproved: boolean;
    /** Pot capacity. Used by the FE to show a virtual split for legacy
     *  lines that haven't been re-saved into the multi-pot model yet. */
    totalAmount?: number;
  } | null;

  /**
   * Multi-pot allocation: each entry is a slice of the line drawn from one
   * pot (or the overflow pot when `bucket === null`). The sum of `amount`
   * across entries equals `estimatedCostExclVat`.
   */
  bucketAllocations?: Array<{
    id?: number;
    bucket: {
      id: number;
      name: string;
      isApproved?: boolean;
      totalAmount?: number;
    } | null;
    amount: number;
  }>;
  estimatedStartDate?: string | null;
  estimatedDeadline?: string | null;
  /**
   * Released lines are locked: their unspent budget went back to the bucket
   * via a RELEASE transaction, the line stays visible for historical stats but
   * can no longer be edited or linked to project parts.
   */
  isReleased?: boolean;
  releasedAt?: string | null;
  releasedByUserId?: string | null;
  releasedAmount?: number | null;
  releasedReason?: string | null;
  auditLogs?: InvestmentItemAuditLog[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Investment item with calculated fields for display in tables
 */
export interface InvestmentItemWithCalculations extends InvestmentItem {
  invoicedAmount: number;
  totalAssigned: number;
  remainingBudget: number;
  projectCount: number;
}

/**
 * DTO for creating a new investment item
 */
export interface CreateInvestmentItemDto {
  title: string;
  description: string;
  buildingId: string;
  unitIds?: string[];
  estimatedCostExclVat: number;
  responsibleUserId: string;
  labelId?: number | null;
  budgetLabel?: InvestmentItemBudgetLabel | null;
  estimatedStartDate?: string | null;
  estimatedDeadline?: string | null;
  /** Optional multi-pot split. Sum must equal estimatedCostExclVat. */
  bucketAllocations?: Array<{ bucketId: number | null; amount: number }>;
}

/**
 * DTO for updating an existing investment item
 */
export interface UpdateInvestmentItemDto {
  title?: string;
  description?: string;
  buildingId?: string;
  unitIds?: string[];
  estimatedCostExclVat?: number;
  status?: InvestmentItemStatus;
  budgetLabel?: InvestmentItemBudgetLabel | null;
  responsibleUserId?: string;
  labelId?: number | null;
  estimatedStartDate?: string | null;
  estimatedDeadline?: string | null;
  bucketAllocations?: Array<{ bucketId: number | null; amount: number }>;
}

/**
 * Unit distribution for budget allocation: { unitId: percentage }
 * Percentages should sum to 100
 */
export type UnitDistribution = Record<string, number>;

/**
 * Allocation from an existing investment item
 */
export interface ItemAllocationDto {
  itemId: number;
  amount: number; // Amount to allocate from this item to the project
  partName?: string; // Custom name for the project part (defaults to investment item title)
  unitIds?: string[]; // Kostenplaatsen - unit IDs for pro-rata distribution
  unitDistribution?: UnitDistribution; // Percentage distribution across units (if multiple units)
  budgetIncreaseAmount?: number; // Amount to increase the investment line budget by (for overflow handling)
  budgetIncreaseReason?: string; // Reason for budget increase (required if budgetIncreaseAmount is set)
}

/**
 * New budget allocation - creates a new investment line AND allocates from it
 */
export interface NewBudgetAllocationDto {
  partName: string; // Name for both the investment line and project part
  labelId: number; // Category label ID
  unitIds: string[]; // Kostenplaatsen - unit IDs for pro-rata distribution
  unitDistribution?: UnitDistribution; // Percentage distribution across units (if multiple units)
  amount: number; // Budget amount (creates investment line with this budget and allocates all of it)
  description?: string; // Description for the investment line
}

/**
 * DTO for converting investment items to a renovation project
 */
export interface ConvertToProjectDto {
  allocations: ItemAllocationDto[];
  newBudgetAllocations?: NewBudgetAllocationDto[]; // New investment lines to create and allocate
  projectTitle: string;
  buildingId: string;
}

/**
 * Dutch label mappings
 */
export const INVESTMENT_PRIORITY_LABELS: Record<InvestmentItemPriority, string> = {
  [InvestmentItemPriority.LOW]: "Laag",
  [InvestmentItemPriority.MEDIUM]: "Gemiddeld",
  [InvestmentItemPriority.HIGH]: "Hoog",
  [InvestmentItemPriority.CRITICAL]: "Kritiek",
};

export const INVESTMENT_STATUS_LABELS: Record<InvestmentItemStatus, string> = {
  [InvestmentItemStatus.ONTWERP]: "Ontwerp",
  [InvestmentItemStatus.IN_BEOORDELING]: "In beoordeling",
  [InvestmentItemStatus.GOEDGEKEURD]: "Goedgekeurd",
  [InvestmentItemStatus.AFGEWEZEN]: "Afgewezen",
  [InvestmentItemStatus.IN_PROJECT]: "In project",
  [InvestmentItemStatus.CLOSED]: "Afgesloten",
};

export const INVESTMENT_BUDGET_LABEL_LABELS: Record<InvestmentItemBudgetLabel, string> = {
  [InvestmentItemBudgetLabel.STRATEGISCH]: "Strategisch budget",
  [InvestmentItemBudgetLabel.ONVOORZIEN]: "Onvoorzien budget",
};

/**
 * Dutch label mappings for audit log change types
 */
export const AUDIT_CHANGE_TYPE_LABELS: Record<AuditLogChangeType, string> = {
  [AuditLogChangeType.STATUS_CHANGE]: "Status wijziging",
  [AuditLogChangeType.BUDGET_CHANGE]: "Budget wijziging",
  [AuditLogChangeType.ALLOCATION_CHANGE]: "Allocatie wijziging",
  [AuditLogChangeType.CREATED]: "Aangemaakt",
  [AuditLogChangeType.BUDGET_TRANSFER]: "Budget ontvangen",
};

// ============================================================================
// API Response Types for Building Investment Items with Assignments
// ============================================================================

/**
 * Response from GET /investment/building/:buildingId/with-assignments
 */
export interface InvestmentItemsWithAssignmentsResponse {
  items: InvestmentItemWithCalculations[];
  totals: {
    totalEstimated: number;
    totalAllocated: number;
    totalAssigned: number;
    totalRemaining: number;
    /**
     * Sum of all INVOICE document lines across every project part on this
     * building, regardless of whether the part is linked to an investment
     * item. Use this for the "Gefactureerd" KPI.
     */
    totalInvoiced: number;
    /**
     * Count of project parts on this building that are not linked to any
     * investment item via PartInvestmentAssignment. Surfaces as a data-quality
     * KPI ("Projectonderdelen niet gelinkt aan budget").
     */
    unlinkedPartsCount?: number;
    /**
     * Sum of `roughBudgetEstimate` (using `lockedRoughBudgetEstimate` when
     * present) across all renovation projects on this building. Feeds the
     * "Ruw geschat budget over projecten" KPI tile.
     */
    totalRoughBudget?: number;
    /**
     * Som van de **goedgekeurde offertes** op dit gebouw (creditnota's
     * negatief). Voedt de tegel "Goedgekeurde offertes" onder *Werkelijke
     * kostprognose*: het deel van het detailbudget dat geen schatting meer is
     * maar een echte prijs.
     */
    totalApprovedQuotes?: number;
  };
}

/**
 * DTO for assigning an investment item to a project part
 */
export interface AssignToProjectPartDto {
  projectId: number;
  partId?: number;
  amount: number;
  notes?: string;
}

/**
 * DTO for updating an assignment
 */
export interface UpdateAssignmentDto {
  amount?: number;
  notes?: string;
}
